<template>
  <div>
    <fts-drawer v-if="visible" position="right" width="440px" @close="beforeClose">
      <!-- header -->
      <template #drawerHeader>
        <div class="info-drawer-header">
          <div class="info-drawer-header-content">
            <h4 class="info-drawer-header-title">{{ isExist ? 'Edit Column' : 'Add Column' }}</h4>
            <!-- <fts-btn icon text @click="beforeClose">
              <font-awesome-icon :icon="['fas', 'times']" />
            </fts-btn> -->
          </div>
          <hr>
        </div>
      </template>

      <!-- body -->
      <template #drawerContent>
        <div class="info-drawer-body">
          <!-- Loading -->
          <div class="info-drawer-loading" v-if="loading">
            <fts-spinner show />
          </div>

          <div class="info-drawer-content" v-else>
            <!-- Name field -->
            <div class="info-form-field">
              <label class="info-form-label">Name</label>
              <input v-model="formData.displayName" type="text" class="info-form-input"
                :class="{ 'info-form-input--error': $v.formData.displayName && $v.formData.displayName.$error }"
                placeholder="Column name" ref="nameInput" @input="onNameInput" />
              <div class="info-form-error" v-if="error('displayName')">
                {{ error('displayName') }}
              </div>
            </div>

            <!-- ai annotation -->
            <fts-ai-annotation :hint="formData.ai_hint" />

            <!-- Type field -->
            <div class="info-form-field">
              <label class="info-form-label">Type</label>
              <fts-dropdown-select ref="typeDropdown" trigger="click" placement="bottom-start" :offset="[0, 4]"
                follow-width append-content-class="info-type-dropdown-content" append-to-body
                :disabled="isExist && !isInternal">
                <template #toggle>
                  <div class="info-type-trigger" :class="{ 'info-type-trigger--disabled': isExist && !isInternal }">
                    <template v-if="isConnectionType && formData.connectionId">
                      <fts-label-group :item-id="formData.connectionId" :list="workspaceModules"
                        icon-class="info-module-emoji" emoji />
                    </template>
                    <template v-else>
                      <i :class="currentTypeIcon" class="info-type-trigger-icon"></i>
                      <span class="info-type-trigger-text">{{ currentTypeName }}</span>
                      <font-awesome-icon :icon="['fas', 'chevron-down']" class="info-type-trigger-arrow" />  
                    </template>  
                  </div>
                </template>
                <template #menu>
                  <div class="info-type-menu">
                    <template v-if="moduleOptions.length">
                      <p class="info-type-title">Available module</p>
                      <div v-for="option in moduleOptions" :key="option.id" class="info-type-option" @click="selectModule(option)">
                        <fts-label-group :item-id="option.id" :list="workspaceModules"
                          icon-class="info-module-emoji" emoji />
                      </div>
                    </template>

                    <div class="info-type-divider"/>
                    <div v-for="infoType in availableTypes" :key="infoType.id" class="info-type-option"
                      :class="{ 'info-type-option--selected': formData.type === infoType.id }"
                      @click="selectType(infoType)">
                      <i :class="infoType.icon" class="info-type-option-icon"></i>
                      <span class="info-type-option-text">{{ infoType.type }}</span>
                      <fts-release-badge :alpha="infoType.isAlpha" class="info-type-option-badge"
                        v-if="infoType.isBeta || infoType.isAlpha" />
                    </div>
                  </div>
                </template>
              </fts-dropdown-select>
              <div class="info-type-hint" v-if="typeHint">{{ typeHint }}</div>
            </div>

            <!-- Connection Type Fields (type 7, 8, 20, 21, 24) -->
            <template v-if="isConnectionType">
              <!-- Module dropdown -->
              <!-- <div class="info-form-field">
                <label class="info-form-label">Module</label>
                <fts-select :form-data="formData"
                  :item="{ fieldName: 'connectionId', icon: 'fts-icon-menu-caret-down' }" :options="moduleOptions"
                  comfort-design @search:focus="preloadModules" @option:selected="updateConnection"
                  @option:clear="updateConnection">
                  <fts-label-group slot="selected-option-container" slot-scope="{ option }" :item-id="option.id"
                    :list="workspaceModules" icon-class="info-module-emoji" emoji />
                  <fts-label-group slot="option" slot-scope="{ option }" :item-id="option.id" :list="workspaceModules"
                    icon-class="info-module-emoji" emoji />
                </fts-select>
                <div class="info-form-error" v-if="$v.formData.connectionId && $v.formData.connectionId.$error">
                  Module is required.
                </div>
              </div> -->

              <!-- Display Information dropdown -->
              <div class="info-form-field" v-if="formData.connectionId">
                <label class="info-form-label">Display Information</label>
                <fts-select :form-data="formData"
                  :item="{ fieldName: 'connectionField', icon: 'fts-icon-menu-caret-down' }" reduceKey="displayName"
                  label="displayName" :options="displayInformationSelection" :key="formData.connectionField"
                  comfort-design @option:selected="updateConnectionField" @option:clear="updateConnectionField">
                  <div slot="selected-option-container" slot-scope="{ option }" class="info-field-option">
                    <i :class="informationIcon(option.type)" class="info-field-option-icon"></i>
                    <span class="info-field-option-name">{{ option.displayName }}</span>
                  </div>
                  <div slot="option" slot-scope="{ option }" class="info-field-option">
                    <i :class="informationIcon(option.type)" class="info-field-option-icon"></i>
                    <span class="info-field-option-name">{{ option.displayName }}</span>
                  </div>
                </fts-select>
                <div class="info-form-error" v-if="$v.formData.connectionField && $v.formData.connectionField.$error">
                  Display Information is required.
                </div>
              </div>
            </template>

            <!-- Select/Tag Type Fields (type 27, 28) -->
            <template v-if="formData.type === 27 || formData.type === 28">
              <!-- Mode switch -->
              <div class="info-select-switch">
                <div class="info-select-switch-option"
                  :class="{ 'info-select-switch-option--active': !formData.reference }"
                  @click="toggleSelectSwitch(false)">
                  User-Defined
                </div>
                <div class="info-select-switch-option"
                  :class="{ 'info-select-switch-option--active': formData.reference }" @click="toggleSelectSwitch(true)"
                  v-if="formData.type === 27 || (isInternal && formData.type === 28)">
                  Reference
                  <fts-release-badge alpha v-if="formData.type === 28" />
                </div>
              </div>

              <!-- Reference Mode Fields -->
              <div class="info-form-card" v-if="formData.reference">
                <!-- Reference Module -->
                <div class="info-form-field">
                  <label class="info-form-label">Reference Module</label>
                  <fts-select :form-data="formData"
                    :item="{ fieldName: 'referenceModuleFieldId', icon: 'fts-icon-menu-caret-down' }"
                    label="displayName" :options="referenceInformation" comfort-design
                    @option:selected="updateReferenceModule" @option:clear="updateReferenceModule">
                    <div slot="selected-option-container" slot-scope="{ option }" class="info-field-option">
                      <i :class="informationIcon(option.type)" class="info-field-option-icon"></i>
                      <span class="info-field-option-name">{{ option.displayName }}</span>
                      <fts-label-group :item="getModule(option.connectionId)" emoji :emoji-size="12"
                        class="info-field-option-module" v-if="option.connectionId && getModule(option.connectionId)" />
                    </div>
                    <div slot="option" slot-scope="{ option }" class="info-field-option">
                      <i :class="informationIcon(option.type)" class="info-field-option-icon"></i>
                      <span class="info-field-option-name">{{ option.displayName }}</span>
                      <fts-label-group :item="getModule(option.connectionId)" emoji :emoji-size="12"
                        class="info-field-option-module" v-if="option.connectionId && getModule(option.connectionId)" />
                    </div>
                  </fts-select>
                </div>

                <!-- Reference Module Table -->
                <div class="info-form-field" v-if="formData.referenceModule">
                  <label class="info-form-label">Reference Table</label>
                  <fts-select :form-data="formData"
                    :item="{ fieldName: 'referenceModuleTable', icon: 'fts-icon-menu-caret-down' }"
                    reduce-key="displayName" label="displayName" :options="referenceModuleTables" comfort-design
                    @search:focus="preloadReferenceModuleTable" @option:selected="updateReferenceModuleTable"
                    @option:clear="updateReferenceModuleTable">
                    <div slot="selected-option-container" slot-scope="{ option }" class="info-field-option">
                      <i :class="informationIcon(option.type)" class="info-field-option-icon"></i>
                      <span class="info-field-option-name">{{ option.displayName }}</span>
                    </div>
                    <div slot="option" slot-scope="{ option }" class="info-field-option">
                      <i :class="informationIcon(option.type)" class="info-field-option-icon"></i>
                      <span class="info-field-option-name">{{ option.displayName }}</span>
                    </div>
                  </fts-select>
                </div>

                <!-- Reference Display Field -->
                <div class="info-form-field" v-if="formData.referenceModuleTable">
                  <label class="info-form-label">Display Field</label>
                  <fts-select :form-data="formData"
                    :item="{ fieldName: 'referenceDisplayField', icon: 'fts-icon-menu-caret-down' }"
                    reduce-key="displayName" label="displayName" :options="referenceDisplayFields" comfort-design
                    @option:selected="updateReferenceDisplayField" @option:clear="updateReferenceDisplayField">
                    <div slot="selected-option-container" slot-scope="{ option }" class="info-field-option">
                      <i :class="informationIcon(option.type)" class="info-field-option-icon"></i>
                      <span class="info-field-option-name">{{ option.displayName }}</span>
                    </div>
                    <div slot="option" slot-scope="{ option }" class="info-field-option">
                      <i :class="informationIcon(option.type)" class="info-field-option-icon"></i>
                      <span class="info-field-option-name">{{ option.displayName }}</span>
                    </div>
                  </fts-select>
                </div>
              </div>

              <!-- User-Defined Options List -->
              <div class="info-form-card" v-else>
                <label class="info-form-label">Options</label>
                <draggable handle=".info-option-handle" draggable=".info-option-item" v-model="formData.optionList"
                  v-bind="dragOptions" class="info-options-list" @start="dragging = true" @end="dragging = false">
                  <div class="info-option-item" v-for="(option, optionIndex) in formData.optionList" :key="option.id">
                    <div class="info-option-wrapper">
                      <font-awesome-icon :icon="['fas', 'grip-vertical']" class="info-option-handle" />
                      <fts-contenteditable v-model="option.name" :style="{ 'background-color': getOptionColor(option) }"
                        placeholder="Option name" class="info-option-name" text-wrap
                        @input="fieldValidatorTouch('optionList', option.id, 'name')" @keydown:enter="addSelectOption"
                        :ref="`option${optionIndex}`" />
                      <div class="info-option-actions">
                        <swatches class="info-option-color" v-model="option.color" row-length="5"
                          :swatches="colorSchemes" shapes="circles" popover-x="left" popover-y="top"
                          :trigger-style="!option.color ? { 'background-color': getOptionColor(option), 'border': 'none' } : null"
                          :swatch-size="20" />
                        <fts-btn icon text @click="removeSelectOption(option)">
                          <font-awesome-icon :icon="['far', 'trash-alt']" />
                        </fts-btn>
                      </div>
                    </div>
                    <div class="info-option-error" v-if="fieldValidatorInvalid('optionList', option.id, 'name')">
                      <template
                        v-if="!(fieldValidator('optionList', option.id, 'name') && fieldValidator('optionList', option.id, 'name').unique)">
                        Option already exists.
                      </template>
                      <template v-else>
                        Option name is required.
                      </template>
                    </div>
                  </div>
                  <fts-btn slot="footer" sm outline color="primary" text-transform-init class="info-option-add"
                    @click="addSelectOption">
                    <font-awesome-icon :icon="['fas', 'plus']" /> Add Option
                  </fts-btn>
                </draggable>

                <!-- Radio toggle (Select type only) -->
                <div class="info-form-field info-form-field--toggle" v-if="!findParent && formData.type === 27">
                  <fts-toggle-switch v-model="formData.radio" :size="18" mat-class />
                  <div class="info-toggle-content">
                    <label class="info-form-label">Radio</label>
                    <div class="info-form-helper">Switch from dropdown to radio buttons.</div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Table/List Type Fields (type 14, 15) -->
            <template v-if="formData.type === 14 || formData.type === 15">
              <div class="info-form-card">
                <label class="info-form-label">Fields</label>
                <draggable handle=".info-field-handle" draggable=".info-field-item" v-model="formData.fields"
                  v-bind="dragOptions" class="info-fields-list" @start="dragging = true" @end="dragging = false">
                  <div class="info-field-item" v-for="field in formData.fields" :key="field.id"
                    @click="openInformationField(field)">
                    <font-awesome-icon :icon="['fas', 'grip-vertical']" class="info-field-handle" @click.stop />
                    <div class="info-field-content">
                      <i :class="informationIcon(field.type)" class="info-field-icon"></i>
                      <span class="info-field-name">{{ field.displayName }}</span>
                    </div>
                    <fts-btn icon text class="info-field-delete" @click.stop="removeInformationField(field)">
                      <font-awesome-icon :icon="['far', 'trash-alt']" />
                    </fts-btn>
                  </div>
                  <fts-btn slot="footer" sm outline color="primary" text-transform-init class="info-field-add"
                    @click="openInformationField">
                    <font-awesome-icon :icon="['fas', 'plus']" /> Add Field
                  </fts-btn>
                </draggable>
                <div class="info-form-error" v-if="$v.formData.fields && $v.formData.fields.$error">
                  At least one field is required.
                </div>
              </div>

              <!-- Simple toggle (Table type only) -->
              <div class="info-form-field info-form-field--toggle" v-if="formData.type === 14">
                <fts-toggle-switch v-model="formData.simple" :size="18" mat-class />
                <div class="info-toggle-content">
                  <label class="info-form-label">Simple</label>
                  <div class="info-form-helper">Use simplified table view.</div>
                </div>
              </div>
            </template>

            <!-- Date Type Fields (type 5, 6) -->
            <template v-if="formData.type === 5 || formData.type === 6">
              <div class="info-form-field">
                <label class="info-form-label">Display Format</label>
                <fts-select :form-data="formData"
                  :item="{ fieldName: 'predefinedFormatId', icon: 'fts-icon-menu-caret-down' }" :options="dateFormats"
                  comfort-design />
              </div>

              <!-- Dashboard Due toggle (beta) -->
              <div class="info-form-field info-form-field--toggle" v-if="isBeta">
                <fts-toggle-switch v-model="formData.dashboardDue" :size="18" mat-class />
                <div class="info-toggle-content">
                  <label class="info-form-label">Dashboard Due <fts-release-badge /></label>
                  <div class="info-form-helper">Set as due date for task dues in dashboard.</div>
                </div>
              </div>
            </template>

            <!-- Number Type Fields (type 3) -->
            <template v-if="formData.type === 3">
              <div class="info-form-field">
                <label class="info-form-label">Decimal Places</label>
                <input v-model.number="formData.decimalPlaces" type="number" min="0" class="info-form-input"
                  placeholder="0" />
              </div>

              <!-- Custom Format (beta) -->
              <div class="info-form-field" v-if="isBeta">
                <label class="info-form-label">Custom Format <fts-release-badge /></label>
                <input v-model="formData.customFormat" type="text" class="info-form-input" placeholder="e.g. 0,0.00" />
              </div>
            </template>

            <!-- File Types (type 10, 11, 12, 13) -->
            <template v-if="[10, 11, 12, 13].indexOf(formData.type) > -1">
              <!-- Max File Size (beta) -->
              <div class="info-form-field" v-if="isBeta">
                <label class="info-form-label">Max File Size (MB) <fts-release-badge /></label>
                <input v-model.number="formData.maxFileSize" type="number" min="0" class="info-form-input"
                  placeholder="No limit" />
              </div>

              <!-- Camera Only (Image types) -->
              <div class="info-form-field info-form-field--toggle" v-if="[10, 11].indexOf(formData.type) > -1">
                <fts-toggle-switch v-model="formData.cameraOnly" :size="18" mat-class />
                <div class="info-toggle-content">
                  <label class="info-form-label">Camera Only</label>
                  <div class="info-form-helper">Only applicable to the App.</div>
                </div>
              </div>
            </template>

            <!-- Text Type Fields (type 0) -->
            <template v-if="formData.type === 0 && !findParent">
              <div class="info-form-field info-form-field--toggle">
                <fts-toggle-switch v-model="formData.uniqueKey" :size="18" mat-class />
                <div class="info-toggle-content">
                  <label class="info-form-label">Unique Key</label>
                  <div class="info-form-helper">Prevent duplicate values in this field.</div>
                </div>
              </div>
            </template>

            <!-- Location Type Fields (type 9) -->
            <template v-if="formData.type === 9 && isInternal">
              <div class="info-form-field info-form-field--toggle">
                <fts-toggle-switch v-model="formData.autocomplete" :size="18" mat-class />
                <div class="info-toggle-content">
                  <label class="info-form-label">Autocomplete <fts-release-badge alpha /></label>
                  <div class="info-form-helper">Enable Google Maps place autocomplete.</div>
                </div>
              </div>
            </template>

            <!-- Advanced Settings -->
            <!-- <fts-collapsible-element v-model="advanced" v-if="showAdvanced">
              <fts-btn slot="toggle" slot-scope="{ isVisible, toggle }" text text-transform-init
                :class="{ open: isVisible }" class="info-advanced-toggle" @click="toggle()">
                Advanced settings
                <font-awesome-icon :icon="['fas', 'caret-right']" class="info-advanced-toggle-icon" />
              </fts-btn>
              <div slot="content" class="info-advanced-content"> 
                <div class="info-form-field" v-if="isInternal">
                  <label class="info-form-label">Field Name <fts-release-badge alpha /></label>
                  <input v-model="formData.fieldName" type="text" class="info-form-input" placeholder="fieldName" />
                  <div class="info-form-error" v-if="$v.formData.fieldName && $v.formData.fieldName.$error">
                    Field name is required and must be unique.
                  </div>
                </div>
 
                <div class="info-form-field" v-if="isTableField">
                  <label class="info-form-label">Column Width</label>
                  <input v-model.number="formData.columnWidth" type="number" min="0" class="info-form-input"
                    placeholder="Auto" />
                </div>
 
                <div class="info-form-field info-form-field--toggle"
                  v-if="isBeta && (formData.type === 7 || formData.type === 20)">
                  <fts-toggle-switch v-model="formData.canAddNew" :size="18" mat-class />
                  <div class="info-toggle-content">
                    <label class="info-form-label">Can Add New <fts-release-badge /></label>
                    <div class="info-form-helper">Enable shortcut for adding new options.</div>
                  </div>
                </div>
 
                <div class="info-form-field info-form-field--toggle" v-if="isBeta && formData.type === 7">
                  <fts-toggle-switch v-model="formData.allowOther" :size="18" mat-class />
                  <div class="info-toggle-content">
                    <label class="info-form-label">Allow Other <fts-release-badge /></label>
                    <div class="info-form-helper">Allow values not in the options.</div>
                  </div>
                </div>
 
                <div class="info-form-field info-form-field--toggle" v-if="isBeta && formData.type === 7">
                  <fts-toggle-switch v-model="formData.wildcard" :size="18" mat-class />
                  <div class="info-toggle-content">
                    <label class="info-form-label">Wildcard <fts-release-badge /></label>
                    <div class="info-form-helper">Allow value from another module via automation.</div>
                  </div>
                </div>
 
                <div class="info-form-field info-form-field--toggle" v-if="isBeta && formData.type === 20">
                  <fts-toggle-switch v-model="formData.assign" :size="18" mat-class />
                  <div class="info-toggle-content">
                    <label class="info-form-label">Assign <fts-release-badge /></label>
                    <div class="info-form-helper">Set value as the assignee.</div>
                  </div>
                </div>
              </div>
            </fts-collapsible-element> -->

          </div>
        </div>
      </template>

      <!-- footer actions -->
      <template #drawerLowerContent v-if="isExist && !loading">
        <div class="info-drawer-actions">
          <fts-btn text text-transform-init @click="remove">
            <font-awesome-icon :icon="['far', 'trash-alt']" /> Delete
          </fts-btn>
          <div class="info-drawer-actions-spacer"></div>
          <!-- <fts-btn outline @click="cancel">
            Cancel
          </fts-btn> -->
        </div>
      </template>
    </fts-drawer>

    <!-- Nested drawer for table fields (only render when not already a child to prevent infinite recursion) -->
    <fts-information-design-drawer v-if="!child && (formData.type === 14 || formData.type === 15)" ref="nestedDrawer"
      :parent="formData" :parent-fields="formData && formData.fields" child />

    <!-- Delete confirmation -->
    <fts-information-delete-confirmation ref="deleteConfirmation" />
  </div>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required } from "vuelidate/lib/validators";

import draggable from "vuedraggable";
import changesets from 'diff-json';
import Swatches from "vue-swatches";
import "vue-swatches/dist/vue-swatches.css";

import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from "../store/canvasDesignStore";

import commons from "../commons/commons";
import validator from "../commons/designValidator";
import { lightenPalette } from "../commons/colors";
import { Information } from '../commons/flags';

import FTSDrawer from "../elements/FTSDrawer";
import FTSBtn from "../elements/FTSBtn";
import FTSDropdownSelect from "../elements/FTSDropdownSelect";
import FTSSelect from "../elements/FTSSelect";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSSpinner from "../elements/FTSSpinner";
import FTSInformationDeleteConfirmation from './FTSInformationDeleteConfirmation';
import FTSAiAnnotation from './FTSAiAnnotation';

const informationReservedKeywords = validator.informationReservedKeywords.concat(["username"]);

export default {
  name: "fts-information-design-drawer",
  components: {
    draggable,
    Swatches,
    "fts-drawer": FTSDrawer,
    "fts-btn": FTSBtn,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-select": FTSSelect,
    "fts-contenteditable": FTSContenteditable,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-label-group": FTSLabelGroup,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-spinner": FTSSpinner,
    "fts-information-delete-confirmation": FTSInformationDeleteConfirmation,
    "fts-ai-annotation": FTSAiAnnotation,
  },
  props: {
    child: Boolean,
    parent: Object,
    parentFields: Array,
  },
  data: () => ({
    visible: false,
    formData: {
      id: null,
      displayName: '',
      type: 0,
    },
    originalData: null,
    resolvePromise: null,
    params: null,
    advanced: false,
    loading: false,
    dragging: false,
    moduleOptions: [],
    referenceModuleTables: [],
    dragOptions: commons.dragOptions,
    dateFormats: commons.dateFormats,
    colorSchemes: lightenPalette,
    hints: [
      { id: Information.Text.ID, hint: Information.Text.Hint },
      { id: Information.YesNo.ID, hint: Information.YesNo.Hint },
      { id: Information.Integer.ID, hint: Information.Integer.Hint },
      { id: Information.Number.ID, hint: Information.Number.Hint },
      { id: Information.Money.ID, hint: Information.Money.Hint },
      { id: Information.Date.ID, hint: Information.Date.Hint },
      { id: Information.DateTime.ID, hint: Information.DateTime.Hint },
      { id: Information.Module.ID, hint: Information.Module.Hint },
      { id: Information.Modules.ID, hint: Information.Modules.Hint },
      { id: Information.Location.ID, hint: Information.Location.Hint },
      { id: Information.Image.ID, hint: Information.Image.Hint },
      { id: Information.Images.ID, hint: Information.Images.Hint },
      { id: Information.File.ID, hint: Information.File.Hint },
      { id: Information.Files.ID, hint: Information.Files.Hint },
      { id: Information.Table.ID, hint: Information.Table.Hint },
      { id: Information.List.ID, hint: Information.List.Hint },
      { id: Information.Username.ID, hint: Information.Username.Hint },
      { id: Information.User.ID, hint: Information.User.Hint },
      { id: Information.Users.ID, hint: Information.Users.Hint },
      { id: Information.Content.ID, hint: Information.Content.Hint },
      { id: Information.Context.ID, hint: Information.Context.Hint },
      { id: Information.Email.ID, hint: Information.Email.Hint },
      { id: Information.Phone.ID, hint: Information.Phone.Hint },
      { id: Information.Select.ID, hint: Information.Select.Hint },
      { id: Information.Tag.ID, hint: Information.Tag.Hint },
      { id: Information.Signature.ID, hint: Information.Signature.Hint },
      { id: Information.DateRange.ID, hint: Information.DateRange.Hint },
      { id: Information.Formula.ID, hint: Information.Formula.Hint },
      { id: Information.URL.ID, hint: Information.URL.Hint },
    ],
  }),
  validations() {
    let formData = {};

    let list = this.information;
    if (this.findParent) {
      list = Array.isArray(this.findParent.fields) ? this.findParent.fields : [];
    } else if (this.parentFields && Array.isArray(this.parentFields)) {
      list = this.parentFields;
    } else if (this.child) {
      list = [];
    }

    formData = validator.informationDesign(list);

    if (!this.isInternal) {
      formData.fieldName = {};
    }

    formData.displayName = {
      ...formData.displayName,
      nonReserved: (val) => validator.isInformationNonReserved(val, informationReservedKeywords)
    };

    // Select/Tag option list validation
    if ((this.formData.type == 27 || this.formData.type == 28) && !this.formData.reference) {
      formData.optionList = {
        $each: {
          name: {
            required,
            unique: (val, item) => {
              if (val && Array.isArray(this.formData.optionList)) {
                const res = this.formData.optionList.filter((x) =>
                  x.name && x.name.toString().trim() == val.toString().trim()
                );
                return !(res.length > 1 && res[0].id !== item.id);
              }
              return true;
            }
          },
          $trackBy: "id",
        }
      };
    }

    // Connection type validation
    if (this.isConnectionType) {
      formData.connectionId = { required };
      formData.connectionField = { required };
    }

    // Table/List fields validation
    if (this.formData.type === 14 || this.formData.type === 15) {
      formData.fields = { required };
    }

    return { formData };
  },
  computed: {
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    isBeta() {
      return authorizationStore.getters.isBeta || canvasDesignStore.getters.workspaceBeta;
    },
    workspaceModules() {
      return this.params && this.params.modules ? this.params.modules : canvasDesignStore.getters.workspaceModules;
    },
    information() {
      return this.params && this.params.information ? this.params.information : canvasDesignStore.getters.canvasInformation;
    },
    findInformation() {
      return _.findDeep(
        this.information,
        (x) => x.id == this.formData.id,
        { childrenPath: "fields" }
      );
    },
    findParent() {
      if (this.parent) {
        return this.parent;
      } else if (this.params && this.params.parentId) {
        const parent = _.findDeep(
          this.information,
          (x) => x.id == this.params.parentId,
          { childrenPath: "fields" }
        );
        if (parent && parent.value && (parent.value.type == 14 || parent.value.type == 15)) {
          return parent.value;
        }
      } else if (
        this.findInformation &&
        this.findInformation.parent &&
        this.findInformation.parent.id &&
        (this.findInformation.parent.type == 14 || this.findInformation.parent.type == 15)
      ) {
        return this.findInformation.parent;
      }
    },
    isExist() {
      return Boolean(this.findInformation);
    },
    isConnectionType() {
      return this.formData && [7, 8, 20, 21, 24].indexOf(this.formData.type) > -1;
    },
    isTableField() {
      return Boolean(this.findParent && this.findParent.type == 14);
    },
    isListField() {
      return Boolean(this.findParent && this.findParent.type == 15);
    },
    availableTypes() {
      return commons.informationTypes.filter(x =>
        ![6, 8, 9, 10, 12, 14, 15, 19, 20, 21, 22, 24, 28, 29, 30, 31, 32, 33, 35, 36, 37].includes(x.id) &&
        !(
          x.internal ||
          (this.isTableField && (x.id === 14 || x.id === 15)) ||
          (this.isListField && x.id === 15) ||
          (x.isAlpha && !this.isInternal) ||
          (x.isBeta && !this.isBeta)
        )
      );
    },
    currentTypeIcon() {
      const infoType = commons.informationTypes.find(t => t.id === this.formData.type);
      return infoType ? infoType.icon : 'fts-icon-text';
    },
    currentTypeName() {
      const infoType = commons.informationTypes.find(t => t.id === this.formData.type);
      return infoType ? infoType.type : 'Text';
    },
    typeHint() {
      const hint = this.hints.find(x => x.id === this.formData.type);
      return hint ? hint.hint : null;
    },
    referenceInformation() {
      const information = this.information.filter(x => x.type == 7 || x.type == 20);
      if (this.findParent && Array.isArray(this.findParent.fields)) {
        const fields = this.findParent.fields.filter(
          x => x.type == 7 || x.type == 20
        ).map(
          y => ({
            ...y,
            parentDisplayName: this.findParent.displayName,
            parentType: this.findParent.type,
          })
        );
        return information.concat(fields);
      }
      return information;
    },
    referenceDisplayFields() {
      try {
        const field = this.referenceModuleTables.find(
          x => x.id === (this.formData && this.formData.referenceModuleTableId)
        );
        if (field && field.fields) return field.fields;
      } catch (err) {
        console.error("InformationDrawer:ReferenceDisplayFields", err);
      }
      return [];
    },
    displayInformationSelection() {
      if (this.formData && this.formData.connectionId) {
        const module = this.moduleOptions.find((x) => x.id == this.formData.connectionId);
        const allowedTypes = [0, 2, 3, 4, 7, 20, 25, 26, 27];
        return Array.isArray(module && module.information)
          ? module.information.reduce((accumulator, currentValue) => {
            if (allowedTypes.indexOf(currentValue.type) > -1 || (this.isInternal && currentValue.type == 16)) {
              return [...accumulator, { ...currentValue }];
            } else if (!Object.hasOwn(currentValue, 'type') && currentValue.fieldName === '$DocumentId') {
              return [...accumulator, { ...currentValue, system: true }];
            }
            return accumulator;
          }, [])
          : [];
      }
      return [];
    },
    showAdvanced() {
      return this.isInternal || this.isTableField ||
        (this.isBeta && (this.formData.type === 7 || this.formData.type === 20));
    },
  },
  methods: {
    async show(item, saveCallBack, continueCallBack, params) {
      this.params = params;
      this.formData = _.cloneDeep(item);
      this.loading = true;
      this.advanced = false;

      // default type
      if(!this.formData.type){
        this.formData.type = 0;
      }

      return new Promise(async (resolve) => {
        this.resolvePromise = resolve;
        this.visible = true;

        // Connection type - load modules
        if (this.isConnectionType) {
          try {
            await this.loadModules();
            const selectedModule = this.moduleOptions.find(x => x.id == this.formData.connectionId);
            if (selectedModule) {
              if (selectedModule.name !== this.formData.connection) {
                this.$set(this.formData, "connection", selectedModule.name);
              }
              if (selectedModule.id !== this.formData.connectionId) {
                this.$set(this.formData, "connectionId", selectedModule.id);
              }
            }
          } catch (err) {
            console.error("InformationDrawer:LoadModules", err);
          }
        }

        // Table simple (default for new)
        if (this.formData.type === 14 && !this.isExist) {
          this.$set(this.formData, "simple", true);
        }

        // Reference type - load reference module table
        if ((this.formData.type === 27 || this.formData.type === 28) && this.formData.reference) {
          if (this.formData.referenceModuleId) {
            await this.loadReferenceModuleTable();
          }

          if (this.formData.referenceModuleFieldId) {
            const field = this.referenceInformation.find(x => x.id === this.formData.referenceModuleFieldId);
            if (field) {
              this.formData.referenceModule = field.displayName;
              this.formData.referenceModuleField = field.fieldName;
            }
          }

          if (this.formData.referenceModuleTableId) {
            const field = this.referenceModuleTables.find(x => x.id === this.formData.referenceModuleTableId);
            if (field) {
              this.formData.referenceModuleTable = field.displayName;
            }
          }

          if (this.formData.referenceDisplayFieldId) {
            const field = this.referenceDisplayFields.find(x => x.id === this.formData.referenceDisplayFieldId);
            if (field) {
              this.formData.referenceDisplayField = field.displayName;
            }
          }
        }

        // Initialize optionList for Select/Tag types if needed
        if ((this.formData.type === 27 || this.formData.type === 28) && !this.formData.reference && !this.formData.optionList) {
          this.$set(this.formData, "optionList", []);
        }

        // Initialize fields for Table/List types if needed
        if ((this.formData.type === 14 || this.formData.type === 15) && !this.formData.fields) {
          this.$set(this.formData, "fields", []);
        }

        this.loading = false;

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          if (this.$refs.nameInput) {
            this.$refs.nameInput.focus();
          }
        });
      });
    },
    reset(){
      this.$v.$reset();
      this.originalData = null;
    },
    beforeClose() {
      /* submit & save */
      this.compactData();
      this.$v.formData.$touch();

      if( this.isExist && this.originalData){
        const diff = changesets.diff(this.formData, this.originalData);
        if (!diff || !diff.length) {
          this.resolvePromise(null);
          this.visible = false;
          this.reset();
          return;
        }
      }
      if (!this.$v.formData.$invalid) {
        const result = _.cloneDeep(this.formData);
        this.resolvePromise(result);
        this.visible = false;
        this.reset();
        return;
      } else if(!this.$v.formData.displayName.required && !this.isExist){
        this.resolvePromise(null);
        this.visible = false;
        this.reset();
        return;
      }

      this.$modal.show("dialog", {
        title: "Confirm discard changes",
        text: "Discard all changes?",
        buttons: [
          { title: "Cancel", handler: () => {
            this.$modal.hide("dialog"); 
          } },
          {
            title: "Discard",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.visible = false;
              this.resolvePromise(null);
              this.reset();
            },
          },
        ],
      });
    },
    async remove() {
      const res = await this.$refs.deleteConfirmation.show({
        canvas: _.cloneDeep(canvasDesignStore.state.canvas),
        information: this.formData,
        payload: this.params && this.params.activityId ? { excluded: [this.params.activityId] } : null
      });

      if (res === true) {
        setTimeout(() => {
          if (this.resolvePromise) {
            this.resolvePromise(true);
            this.resolvePromise = null;
          }
          this.visible = false;
          this.$v.$reset();
          Object.assign(this.$data, this.$options.data.apply(this));
        }, 300);
      }
    },
    compactData() {
      // Clean up based on type
      if (this.formData.type != 0) {
        this.$delete(this.formData, "uniqueKey");
      }

      if (!this.isConnectionType) {
        this.updateConnection();
      }

      if (this.formData.type != 27 && this.formData.type != 28) {
        this.$delete(this.formData, "optionList");
        this.$delete(this.formData, "reference");
        this.updateReferenceModule();
      } else {
        if (this.formData.reference === true) {
          this.$delete(this.formData, "optionList");
        } else {
          this.$delete(this.formData, "reference");
          this.updateReferenceModule();
        }
      }

      if (this.formData.type !== 14 && this.formData.type !== 15) {
        this.$delete(this.formData, "fields");
        this.$delete(this.formData, "simple");
      }
    },
    error(key) {
      if (
        key === "displayName" &&
        this.$v.formData.displayName &&
        this.$v.formData.displayName.$error &&
        !this.$v.formData.displayName.nonReserved
      ) {
        const reducer = (res, val, ind, arr) =>
          ind ? (ind === arr.length - 1 ? `${res}, and ${val}` : `${res}, ${val}`) : val;
        return `Cannot use reserved words: ${informationReservedKeywords.reduce(reducer, null)}`;
      }
      return validator.informationDesignError(this.$v.formData, key);
    },
    onNameInput() {
      // Clear touched state on input
    },
    selectModule(option){
      this.selectType({ id: 7, type: 'Module' });
      debugger
      this.updateConnection(option);
    },
    selectType(infoType) {
      const oldType = this.formData.type;
      this.formData.type = infoType.id;

      // Initialize type-specific data
      if (infoType.id === 14) {
        this.$set(this.formData, "simple", true);
        if (!this.formData.fields) this.$set(this.formData, "fields", []);
      } else if (infoType.id === 15) {
        if (!this.formData.fields) this.$set(this.formData, "fields", []);
      } else if (infoType.id === 27 || infoType.id === 28) {
        if (!this.formData.optionList) this.$set(this.formData, "optionList", []);
      }

      // Reset connection when type changes
      if (oldType !== infoType.id && this.isConnectionType) {
        this.updateConnection();
      }

      if (this.$refs.typeDropdown && this.$refs.typeDropdown.$refs.popper) {
        this.$refs.typeDropdown.$refs.popper.doClose();
      }
    },
    informationIcon(id) {
      return commons.informationIcon(id) || "fts-icon-text";
    },
    getModule(id) {
      if (id && this.workspaceModules) {
        const module = this.workspaceModules.find(x => x.id == id);
        if (module) {
          return { id: module.id, name: module.name, emoji: module.emoji };
        }
      }
    },
    // Connection methods
    async loadModules() {
      try {
        const res = await canvasDesignStore.dispatch("loadVectorSelection");
        const moduleOptions = res.data ? res.data : [];
        if (this.formData.type === 20 || this.formData.type === 21) {
          this.moduleOptions = moduleOptions.filter((x) => x.isUserCollection);
        } else {
          this.moduleOptions = moduleOptions;
        }
      } catch (err) {
        this.moduleOptions = [];
        console.error("InformationDrawer:LoadModules", err);
      }
    },
    async preloadModules(loading) {
      loading(true);
      await this.loadModules();
      loading(false);
    },
    updateConnection(item) {
      if(item && item.id){
        this.$set(this.formData, "connectionId", item.id);
      }

      if (item && item.name) {
        this.$set(this.formData, "connection", item.name);
      } else if (this.formData.connection) {
        this.$delete(this.formData, "connection");
      }
      if (!item && this.formData.connectionId) {
        this.$delete(this.formData, "connectionId");
      }
      this.updateConnectionField();
    },
    updateConnectionField(item) {
      if (item && item.id) {
        this.$set(this.formData, "connectionFieldId", item.id);
      } else {
        this.$delete(this.formData, "connectionFieldId");
      }
      if (item && item.fieldName) {
        this.$set(this.formData, "connectionFieldName", item.fieldName);
      } else if (this.formData.connectionFieldName) {
        this.$delete(this.formData, "connectionFieldName");
      }
      if (!item && this.formData.connectionField) {
        this.$delete(this.formData, "connectionField");
      }
    },
    // Select/Tag methods
    toggleSelectSwitch(val) {
      if (Boolean(val)) {
        this.$set(this.formData, "reference", true);
      } else {
        this.$delete(this.formData, "reference");
        this.updateReferenceModule();
      }
    },
    addSelectOption() {
      if (this.$v.formData.optionList) {
        this.$v.formData.optionList.$touch();
        if (this.$v.formData.optionList.$error) return;
      }

      if (!this.formData.optionList) {
        this.$set(this.formData, "optionList", []);
      }

      const option = {
        id: commons.newUUID(),
        name: null,
      };
      this.formData.optionList.push(option);

      this.$nextTick(() => {
        const ref = this.$refs[`option${this.formData.optionList.indexOf(option)}`];
        if (ref && ref[0] && ref[0].$el) {
          ref[0].$el.focus();
        }
      });
    },
    removeSelectOption(item) {
      this.formData.optionList.splice(this.formData.optionList.indexOf(item), 1);
    },
    getOptionColor(item) {
      return commons.selectOptionBackground({
        option: item,
        optionList: this.formData.optionList,
      });
    },
    // Reference methods
    async loadReferenceModuleTable() {
      try {
        const res = await canvasDesignStore.dispatch("loadModuleInformation", this.formData.referenceModuleId);
        this.referenceModuleTables = res.filter(x => x.type == 14);
      } catch (err) {
        this.referenceModuleTables = [];
        console.error("InformationDrawer:LoadReferenceModuleTable", err);
      }
    },
    async preloadReferenceModuleTable(loading) {
      loading(true);
      await this.loadReferenceModuleTable();
      loading(false);
    },
    async updateReferenceModule(item) {
      this.updateReferenceModuleTable();
      if (item && item.displayName) {
        this.$set(this.formData, "referenceModule", item.displayName);
        this.$set(this.formData, "referenceModuleId", item.connectionId);
        this.$set(this.formData, "referenceModuleField", item.fieldName);
        await this.loadReferenceModuleTable();
        if (this.referenceModuleTables.length) {
          this.$set(this.formData, "referenceModuleTable", this.referenceModuleTables[0].displayName);
          this.updateReferenceModuleTable(this.referenceModuleTables[0]);
        }
      } else {
        this.$delete(this.formData, "referenceModule");
        this.$delete(this.formData, "referenceModuleId");
        this.$delete(this.formData, "referenceModuleField");
        this.$delete(this.formData, "referenceModuleFieldId");
      }
    },
    updateReferenceModuleTable(item) {
      this.updateReferenceDisplayField();
      if (item && item.id) {
        this.$set(this.formData, "referenceModuleTableId", item.id);
        if (this.referenceDisplayFields.length) {
          this.$set(this.formData, "referenceDisplayField", this.referenceDisplayFields[0].displayName);
          this.updateReferenceDisplayField(this.referenceDisplayFields[0]);
        }
      } else {
        this.$delete(this.formData, "referenceModuleTable");
        this.$delete(this.formData, "referenceModuleTableId");
      }
    },
    updateReferenceDisplayField(item) {
      if (item && item.id) {
        this.$set(this.formData, "referenceDisplayFieldId", item.id);
      } else {
        this.$delete(this.formData, "referenceDisplayField");
        this.$delete(this.formData, "referenceDisplayFieldId");
      }
    },
    // Table/List field methods
    async openInformationField(selected) {
      let item = { ...selected };

      if (!item.id) {
        item = {
          id: commons.generateFieldName(),
          fieldName: commons.generateFieldName(),
          type: 0,
        };
      }

      const vm = this;
      let items = this.formData.fields;

      function set(result) {
        if (!items) {
          vm.$set(vm.formData, "fields", []);
          items = vm.formData.fields;
        }
        const findIndex = items.findIndex((x) => x.id == item.id);
        if (findIndex > -1) {
          vm.$set(items, findIndex, result);
        } else {
          items.push(result);
        }
      }

      // Wait for nested drawer to be rendered if type just changed
      await this.$nextTick();

      if (!this.$refs.nestedDrawer) {
        console.warn("InformationDrawer: nestedDrawer not available");
        return;
      }

      const result = await this.$refs.nestedDrawer.show(item, null, null, {
        disabledDelete: this.params && this.params.disabledDelete
      });

      if (result != null && result != true) {
        set(result);
      } else if (result == true) {
        const findIndex = items.findIndex((x) => x.id == item.id);
        if (findIndex > -1) items.splice(findIndex, 1);
      }
    },
    async removeInformationField(item) {
      const res = await this.$refs.deleteConfirmation.show({
        canvas: _.cloneDeep(canvasDesignStore.state.canvas),
        information: item,
        payload: this.params && this.params.activityId ? { excluded: [this.params.activityId] } : null
      });

      if (res === true) {
        this.formData.fields.splice(this.formData.fields.indexOf(item), 1);
      }
    },
    // Validation helpers
    fieldValidator(parentFieldName, fieldId, fieldName) {
      try {
        return validator.fieldValidator(
          this.$v.formData[parentFieldName].$each.$iter,
          fieldId,
          fieldName
        );
      } catch (err) {
        console.error("InformationDrawer:FieldValidator", err);
      }
    },
    fieldValidatorTouch(parentFieldName, fieldId, fieldName) {
      try {
        validator.fieldValidatorTouch(
          this.$v.formData[parentFieldName].$each.$iter,
          fieldId,
          fieldName
        );
      } catch (err) {
        console.error("InformationDrawer:FieldValidatorTouch", err);
      }
    },
    fieldValidatorInvalid(parentFieldName, fieldId, fieldName) {
      try {
        return validator.fieldValidatorInvalid(
          this.$v.formData[parentFieldName].$each.$iter,
          fieldId,
          fieldName
        );
      } catch (err) {
        console.error("InformationDrawer:FieldValidatorInvalid", err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.info-drawer-header {
  padding-top: 1rem;

  hr {
    margin: 0;
    border: 0;
    border-top: 1px solid var(--fts-border);
  }
}

.info-drawer-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
}

.info-drawer-header-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--fts-text);
  margin-bottom: 10px;
}

.info-drawer-body {
  height: 100%;
  width: 100%;
  overflow: auto;
}

.info-drawer-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.info-drawer-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.info-form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &--toggle {
    flex-direction: row;
    align-items: flex-start;
    gap: 0.75rem;
  }
}

.info-toggle-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--fts-text);
}

.info-form-helper {
  font-size: 0.75rem;
  color: var(--fts-default);
  line-height: 1.4;
}

.info-form-input {
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  color: var(--fts-text);
  background-color: var(--fts-background);
  border: 1px solid var(--fts-border);
  border-radius: 0.25rem;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--fts-primary);
  }

  &::placeholder {
    color: var(--fts-text-secondary);
  }

  &--error {
    border-color: var(--fts-danger);
  }
}

.info-form-error {
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  color: var(--fts-danger);
  background-color: rgba(var(--fts-danger-rgb), 0.1);
  border-radius: 0.25rem;
  border-left: 3px solid var(--fts-danger);
}

.info-form-card {
  padding: 1rem;
  background-color: var(--fts-gray-50);
  border: 1px solid var(--fts-border);
  border-radius: 0.375rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-type-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background-color: var(--fts-background);
  border: 1px solid var(--fts-border);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover:not(.info-type-trigger--disabled) {
    border-color: var(--fts-primary);
  }

  &--disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.info-type-trigger-icon {
  color: var(--inistate-text-secondary-color);
}

.info-type-trigger-text {
  flex: 1;
  font-size: 0.875rem;
  color: var(--fts-text);
}

.info-type-trigger-arrow {
  color: var(--fts-text-secondary);
  font-size: 0.75rem;
}

.info-type-hint {
  font-size: 0.75rem;
  color: var(--fts-default);
  margin-top: -0.25rem;
}

// Select/Tag switch
.info-select-switch {
  display: flex;
  gap: 0.375rem;
}

.info-select-switch-option {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  background: rgba(var(--fts-gray-100-rgb), 0.5);
  color: var(--fts-default);
  transition: all 0.15s ease;

  &:hover {
    background: rgba(var(--fts-gray-100-rgb), 0.75);
  }

  &--active {
    background: rgba(var(--inistate-color-primary-rgb), 0.1);
    color: var(--fts-primary);
  }
}

// Options list
.info-options-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-option-item {
  display: flex;
  flex-direction: column;
}

.info-option-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-option-handle {
  color: var(--fts-default);
  font-size: 0.75rem;
  cursor: grab;
  padding: 0.25rem;

  &:hover {
    color: var(--fts-text);
  }
}

.info-option-name {
  flex: 1;
  padding: 0.375rem 0.5rem;
  font-size: 0.875rem;
  border-radius: 0.25rem;
  min-width: 0;
}

.info-option-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.info-option-color {
  &.vue-swatches {
    .vue-swatches__trigger {
      height: 20px !important;
      width: 20px !important;
      min-width: 20px;
    }
  }
}

.info-option-error {
  font-size: 0.75rem;
  color: var(--fts-danger);
  margin-top: 0.25rem;
  margin-left: 1.5rem;
}

.info-option-add {
  margin-top: 0.5rem;
}

// Fields list
.info-fields-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-field-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(var(--fts-gray-100-rgb), 0.5);

    .info-field-handle,
    .info-field-delete {
      opacity: 1;
    }
  }
}

.info-field-handle {
  color: var(--fts-default);
  font-size: 0.75rem;
  cursor: grab;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.info-field-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.info-field-icon {
  color: var(--inistate-text-secondary-color);
}

.info-field-name {
  font-size: 0.875rem;
  color: var(--fts-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.info-field-delete {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.info-field-add {
  margin-top: 0.5rem;
}

// Field option (in dropdowns)
.info-field-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.info-field-option-icon {
  color: var(--inistate-text-secondary-color);
  flex-shrink: 0;
}

.info-field-option-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.info-field-option-module {
  flex-shrink: 0;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  background-color: var(--fts-gray-100);
  font-size: 0.75rem;
}

.info-module-emoji {
  margin-right: 0.5rem;
}

// Advanced settings
.info-advanced-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--fts-default);
  padding: 0;

  &.open {
    .info-advanced-toggle-icon {
      transform: rotate(90deg);
    }
  }
}

.info-advanced-toggle-icon {
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

.info-advanced-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
}

// Footer
.info-drawer-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--fts-border);
}

.info-drawer-actions-spacer {
  flex: 1;
}
</style>

<!-- Non-scoped styles for dropdown menu (rendered outside component via append-to-body) -->
<style lang="scss">
.info-type-dropdown-content {
  width: 100% !important;
  min-width: 100% !important;
  max-width: none !important;
  box-sizing: border-box;
}

.info-type-menu {
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
}

.info-type-title {
  padding: 0.625rem 0.75rem;
  font-size: 10px;
  font-weight: 400;
  color: var(--inistate-text-placeholder);
}

.info-type-divider{
  // color: var(--inistate-border-primary);
  height: 1px;
  width: 100%;
  background: var(--inistate-border-primary);
}

.info-type-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(var(--fts-gray-100-rgb), 0.75);
  }

  &--selected {
    background-color: rgba(var(--inistate-color-primary-rgb), 0.1);
  }
}

.info-type-option-icon {
  color: var(--inistate-text-secondary-color);
  width: 1rem;
  text-align: center;
}

.info-type-option-text {
  font-size: 0.875rem;
  color: var(--fts-text);
}

.info-type-option-badge {
  margin-left: auto;
}

// vue-swatches styles
.info-option-color .vue-swatches__container {
  position: absolute;
  right: 0;
  z-index: 9999;
}
</style>
